import type { DbExecutor } from "../executor";
import { generateId } from "../ids";
import { validateDesignName, type Design, type DesignKind } from "../../domain/design";
import { ValidationError } from "./projectRepository";

interface DesignRow {
  id: string;
  project_id: string;
  name: string;
  kind: string;
  source_design_id: string | null;
  sort_order: number;
  notes: string;
  archived_at: string | null;
  author_account_id: string | null;
  revision: number;
  created_at: string;
  updated_at: string;
}

function mapDesign(r: DesignRow): Design {
  return {
    id: r.id,
    projectId: r.project_id,
    name: r.name,
    kind: r.kind as DesignKind,
    sourceDesignId: r.source_design_id,
    sortOrder: Number(r.sort_order),
    notes: r.notes,
    archivedAt: r.archived_at,
    authorAccountId: r.author_account_id ?? null,
    revision: Number(r.revision ?? 0),
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

const SELECT = `SELECT id, project_id, name, kind, source_design_id, sort_order, notes, archived_at, author_account_id, revision, created_at, updated_at FROM designs`;

/**
 * Design scenarios (spec §17). Duplication copies geometry with NEW UUIDs
 * and per-entity lineage (origin_id = source entity's origin, else its id)
 * so scenarios stay related without sharing rows. The Existing design is
 * created lazily for projects that predate it (migration seeds one per
 * existing project).
 */
export class DesignRepository {
  constructor(private readonly db: DbExecutor) {}

  async listByProject(projectId: string): Promise<Design[]> {
    const rows = await this.db.select<DesignRow>(
      `${SELECT} WHERE project_id = ? ORDER BY CASE kind WHEN 'existing' THEN 0 ELSE 1 END, sort_order, created_at`,
      [projectId],
    );
    return rows.map(mapDesign);
  }

  async getById(id: string): Promise<Design | null> {
    const rows = await this.db.select<DesignRow>(`${SELECT} WHERE id = ?`, [id]);
    return rows.length === 0 ? null : mapDesign(rows[0]);
  }

  /** The project's canonical Existing design (created if missing). */
  async ensureExisting(projectId: string): Promise<Design | null> {
    const rows = await this.db.select<DesignRow>(`${SELECT} WHERE project_id = ? AND kind = 'existing' ORDER BY created_at LIMIT 1`, [projectId]);
    if (rows.length > 0) return mapDesign(rows[0]);
    // New construction (spec §12A): there is no meaningful existing building
    // and the software NEVER fabricates one. All designs are Proposed.
    const type = await this.db.select<{ project_type: string }>("SELECT project_type FROM projects WHERE id = ?", [projectId]);
    if ((type[0]?.project_type ?? "renovation") === "new_construction") return null;
    const now = new Date().toISOString();
    const design: Design = {
      id: generateId(),
      projectId,
      name: "Existing",
      kind: "existing",
      sourceDesignId: null,
      sortOrder: 0,
      notes: "",
      archivedAt: null,
      authorAccountId: null,
      revision: 0,
      createdAt: now,
      updatedAt: now,
    };
    try {
      await this.db.execute(
        `INSERT INTO designs (id, project_id, name, kind, source_design_id, sort_order, notes, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [design.id, projectId, design.name, design.kind, null, 0, "", now, now],
      );
    } catch {
      // Concurrent ensureExisting lost the race — the unique index
      // (v11: one Existing per project) guarantees a winner exists.
      const winner = await this.db.select<DesignRow>(`${SELECT} WHERE project_id = ? AND kind = 'existing' ORDER BY created_at LIMIT 1`, [projectId]);
      return winner.length > 0 ? mapDesign(winner[0]) : null;
    }
    await this.db.execute(`UPDATE projects SET active_design_id = ? WHERE id = ? AND active_design_id IS NULL`, [design.id, projectId]);
    return design;
  }

  /**
   * Create a Proposed design: blank, or a full duplicate of `sourceDesignId`
   * (geometry across every floor + room anchors) with new UUIDs and lineage.
   */
  async createProposed(
    projectId: string,
    name: string,
    sourceDesignId: string | null,
    authorAccountId: string | null = null,
  ): Promise<Design> {
    const problems = validateDesignName(name);
    if (problems.length > 0) throw new ValidationError(problems);
    const now = new Date().toISOString();
    const existing = await this.listByProject(projectId);
    const maxSort = existing.reduce((m, d) => Math.max(m, d.sortOrder), 0);
    const design: Design = {
      id: generateId(),
      projectId,
      name: name.trim(),
      kind: "proposed",
      sourceDesignId,
      sortOrder: maxSort + 10,
      notes: "",
      archivedAt: null,
      authorAccountId,
      revision: 0,
      createdAt: now,
      updatedAt: now,
    };
    await this.db.transaction(async () => {
      await this.db.execute(
        `INSERT INTO designs (id, project_id, name, kind, source_design_id, sort_order, notes, author_account_id, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [design.id, projectId, design.name, design.kind, design.sourceDesignId, design.sortOrder, "", authorAccountId, now, now],
      );
      if (sourceDesignId) {
        const source = await this.getById(sourceDesignId);
        if (!source || source.projectId !== projectId) throw new ValidationError(["The source design no longer exists."]);
        const wallMap = await this.copyGeometry(sourceDesignId, design.id, now);
        const { featureMap, componentMap } = await this.copyScenarioPlanning(projectId, sourceDesignId, design.id, now);
        await this.copyObjects(sourceDesignId, design.id, wallMap, featureMap, now);
        void componentMap;
      }
      // Phase 8.1: the FIRST Proposed design becomes the Active Design
      // automatically. Existing is only the default active while a project
      // has nothing but documentation — the moment the user starts
      // planning, the working Scope & Estimate must follow their plan, or
      // Design work looks like it "adds no cost". Later designs never
      // steal Active (explicit "Set as Active Design" only).
      await this.db.execute(
        `UPDATE projects SET active_design_id = ?
          WHERE id = ? AND (active_design_id IS NULL OR active_design_id IN
            (SELECT id FROM designs WHERE project_id = ? AND kind = 'existing'))`,
        [design.id, projectId, projectId],
      );
    });
    return design;
  }

  /**
   * Copy the source design's SCENARIO planning tree (features → assembly
   * instances/options/components → generated + attached lines → product
   * selections) with new UUIDs and feature-level lineage. Common scope
   * (design_id NULL) is shared, never copied. Actual accounting is never
   * touched: expense links stay with the ORIGINAL design's lines.
   */
  private async copyScenarioPlanning(
    projectId: string,
    fromDesignId: string,
    toDesignId: string,
    now: string,
  ): Promise<{ featureMap: Map<string, string>; componentMap: Map<string, string> }> {
    const featureMap = new Map<string, string>();
    const componentMap = new Map<string, string>();
    const features = await this.db.select<Record<string, unknown>>(
      "SELECT * FROM project_features WHERE project_id = ? AND design_id = ? AND archived_at IS NULL",
      [projectId, fromDesignId],
    );
    for (const f of features) {
      const id = generateId();
      featureMap.set(f.id as string, id);
      await this.db.execute(
        `INSERT INTO project_features (id, project_id, design_id, origin_id, room_id, floor_id, feature_definition_id, name, action,
           quantity_x100, quantity_unit, quantity_source, quantity_geometry_binding, quantity_geometry_x100, notes, sort_order, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          id, projectId, toDesignId, (f.origin_id as string | null) ?? (f.id as string), f.room_id, f.floor_id, f.feature_definition_id,
          f.name, f.action, f.quantity_x100, f.quantity_unit, f.quantity_source, f.quantity_geometry_binding, f.quantity_geometry_x100,
          f.notes, f.sort_order, now, now,
        ],
      );
    }
    if (featureMap.size === 0) return { featureMap, componentMap };
    const featureIds = [...featureMap.keys()];
    const ph = featureIds.map(() => "?").join(",");
    // assemblies
    const instances = await this.db.select<Record<string, unknown>>(
      `SELECT * FROM assembly_instances WHERE project_feature_id IN (${ph}) AND archived_at IS NULL`,
      featureIds,
    );
    const instanceMap = new Map<string, string>();
    const optionMap = new Map<string, string>();
    for (const i of instances) {
      const id = generateId();
      instanceMap.set(i.id as string, id);
      await this.db.execute(
        `INSERT INTO assembly_instances (id, project_feature_id, template_id, name, driving_unit, notes, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
        [id, featureMap.get(i.project_feature_id as string), i.template_id, i.name, i.driving_unit, i.notes, now, now],
      );
      const options = await this.db.select<Record<string, unknown>>("SELECT * FROM assembly_instance_options WHERE instance_id = ?", [i.id]);
      for (const o of options) {
        const oid = generateId();
        optionMap.set(o.id as string, oid);
        await this.db.execute("INSERT INTO assembly_instance_options (id, instance_id, prompt, value, sort_order, automatic_finish_default) VALUES (?, ?, ?, ?, ?, ?)", [
          oid, id, o.prompt, o.value, o.sort_order, i.updated_at === i.created_at ? o.automatic_finish_default ?? null : null,
        ]);
      }
      const components = await this.db.select<Record<string, unknown>>("SELECT * FROM assembly_instance_components WHERE instance_id = ?", [i.id]);
      for (const c of components) {
        const cid = generateId();
        componentMap.set(c.id as string, cid);
        await this.db.execute(
          `INSERT INTO assembly_instance_components (id, instance_id, name, component_type, role, inclusion, condition_option_id, included,
             qty_basis, fixed_qty_x100, multiplier_x10000, waste_pct_x100, unit, unit_cost_cents, labor_minutes_x100, price_source, taxable, category_id,
             qty_override_x100, material_definition_id, automatic_demolition_default, notes, sort_order, archived_at, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            cid, id, c.name, c.component_type, c.role, c.inclusion,
            c.condition_option_id ? optionMap.get(c.condition_option_id as string) ?? null : null,
            c.included, c.qty_basis, c.fixed_qty_x100, c.multiplier_x10000, c.waste_pct_x100, c.unit, c.unit_cost_cents, c.labor_minutes_x100 ?? null,
            c.price_source, c.taxable, c.category_id, c.qty_override_x100 ?? null, c.material_definition_id ?? null,
            i.updated_at === i.created_at && c.updated_at === c.created_at ? c.automatic_demolition_default ?? null : null,
            c.notes, c.sort_order, c.archived_at ?? null, now, now,
          ],
        );
      }
    }
    // proposed lines attached to the copied features (generated + manual-on-feature)
    const lines = await this.db.select<Record<string, unknown>>(`SELECT * FROM proposed_cost_lines WHERE feature_id IN (${ph})`, featureIds);
    for (const l of lines) {
      await this.db.execute(
        `INSERT INTO proposed_cost_lines (id, project_id, description, amount_cents, category_id, cost_type, feature_id, assembly_component_id,
           source, sort_order, notes, archived_at, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          generateId(), l.project_id, l.description, l.amount_cents, l.category_id, l.cost_type,
          featureMap.get(l.feature_id as string),
          l.assembly_component_id ? componentMap.get(l.assembly_component_id as string) ?? null : null,
          l.source, l.sort_order, l.notes, l.archived_at ?? null, now, now,
        ],
      );
    }
    // product selections on copied components (active snapshots only)
    if (componentMap.size > 0) {
      const compIds = [...componentMap.keys()];
      const cph = compIds.map(() => "?").join(",");
      const selections = await this.db.select<Record<string, unknown>>(
        `SELECT * FROM project_product_selections WHERE component_id IN (${cph}) AND replaced_at IS NULL`,
        compIds,
      );
      for (const sel of selections) {
        await this.db.execute(
          `INSERT INTO project_product_selections (id, component_id, product_id, price_observation_id, product_name_snapshot, supplier_name_snapshot,
             purchase_unit_snapshot, coverage_per_unit_x100_snap, coverage_unit_snapshot, price_cents_snapshot, price_includes_tax_snapshot,
             price_source_snapshot, price_observed_at_snapshot, round_separately, purchase_qty_override, created_at, replaced_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, NULL)`,
          [
            generateId(), componentMap.get(sel.component_id as string), sel.product_id, sel.price_observation_id,
            sel.product_name_snapshot, sel.supplier_name_snapshot, sel.purchase_unit_snapshot, sel.coverage_per_unit_x100_snap,
            sel.coverage_unit_snapshot, sel.price_cents_snapshot, sel.price_includes_tax_snapshot, sel.price_source_snapshot,
            sel.price_observed_at_snapshot, sel.round_separately, sel.purchase_qty_override, now,
          ],
        );
      }
    }
    return { featureMap, componentMap };
  }

  /** Copy ACTIVE plan objects with new UUIDs, root lineage, and remapped hosts/features/parents. */
  private async copyObjects(
    fromDesignId: string,
    toDesignId: string,
    wallMap: Map<string, string>,
    featureMap: Map<string, string>,
    now: string,
  ): Promise<void> {
    const objects = await this.db.select<Record<string, unknown>>(
      "SELECT * FROM plan_objects WHERE design_id = ? AND archived_at IS NULL",
      [fromDesignId],
    );
    const objectMap = new Map<string, string>();
    for (const o of objects) objectMap.set(o.id as string, generateId());
    for (const o of objects) {
      await this.db.execute(
        `INSERT INTO plan_objects (id, design_id, floor_id, definition_id, geometry_type, x, y, end_x, end_y, rotation_x10, width, depth,
           height_in, mount_height_in, parent_object_id, detached, host_wall_id, host_offset, host_side, room_id, feature_id, origin_id,
           notes, archived_at, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, NULL, ?, ?)`,
        [
          objectMap.get(o.id as string), toDesignId, o.floor_id, o.definition_id, o.geometry_type, o.x, o.y, o.end_x, o.end_y,
          o.rotation_x10, o.width, o.depth, o.height_in, o.mount_height_in,
          o.parent_object_id ? objectMap.get(o.parent_object_id as string) ?? null : null,
          o.detached,
          o.host_wall_id ? wallMap.get(o.host_wall_id as string) ?? null : null,
          o.host_offset, o.host_side, o.room_id,
          o.feature_id ? featureMap.get(o.feature_id as string) ?? null : null,
          (o.origin_id as string | null) ?? (o.id as string),
          o.notes, now, now,
        ],
      );
    }
  }

  /** Copy ACTIVE geometry from one design to another (new UUIDs, lineage kept). */
  private async copyGeometry(fromDesignId: string, toDesignId: string, now: string): Promise<Map<string, string>> {
    const nodes = await this.db.select<{ id: string; floor_id: string; x: number; y: number; origin_id: string | null }>(
      "SELECT id, floor_id, x, y, origin_id FROM plan_nodes WHERE design_id = ? AND archived_at IS NULL",
      [fromDesignId],
    );
    const nodeMap = new Map<string, string>();
    for (const n of nodes) {
      const id = generateId();
      nodeMap.set(n.id, id);
      await this.db.execute(
        "INSERT INTO plan_nodes (id, floor_id, design_id, x, y, origin_id, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?)",
        [id, n.floor_id, toDesignId, n.x, n.y, n.origin_id ?? n.id, now, now],
      );
    }
    const walls = await this.db.select<{
      id: string;
      floor_id: string;
      kind: string;
      start_node_id: string;
      end_node_id: string;
      thickness: number;
      height_inches: number | null;
      exterior_override: number | null;
      origin_id: string | null;
      notes: string;
    }>(
      "SELECT id, floor_id, kind, start_node_id, end_node_id, thickness, height_inches, exterior_override, origin_id, notes FROM walls WHERE design_id = ? AND archived_at IS NULL",
      [fromDesignId],
    );
    const wallMap = new Map<string, string>();
    for (const w of walls) {
      const id = generateId();
      wallMap.set(w.id, id);
      await this.db.execute(
        `INSERT INTO walls (id, floor_id, design_id, kind, start_node_id, end_node_id, thickness, height_inches, exterior_override, origin_id, notes, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [id, w.floor_id, toDesignId, w.kind, nodeMap.get(w.start_node_id), nodeMap.get(w.end_node_id), w.thickness, w.height_inches, w.exterior_override, w.origin_id ?? w.id, w.notes, now, now],
      );
    }
    const openings = await this.db.select<{
      id: string;
      wall_id: string;
      kind: string;
      offset_from_start: number;
      width: number;
      height: number;
      sill_height: number;
      swing: string | null;
      origin_id: string | null;
      notes: string;
      // A copy is the same opening: a double window stays a double window
      // (spec §60G). Columns added later must be added HERE too.
      window_config: string;
      window_operation: string;
    }>(
      `SELECT o.id, o.wall_id, o.kind, o.offset_from_start, o.width, o.height, o.sill_height, o.swing, o.origin_id, o.notes,
              o.window_config, o.window_operation
         FROM wall_openings o JOIN walls w ON w.id = o.wall_id WHERE w.design_id = ? AND o.archived_at IS NULL AND w.archived_at IS NULL`,
      [fromDesignId],
    );
    for (const o of openings) {
      await this.db.execute(
        `INSERT INTO wall_openings (id, wall_id, kind, offset_from_start, width, height, sill_height, swing, origin_id, notes, created_at, updated_at, window_config, window_operation)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [generateId(), wallMap.get(o.wall_id), o.kind, o.offset_from_start, o.width, o.height, o.sill_height, o.swing, o.origin_id ?? o.id, o.notes, now, now, o.window_config, o.window_operation],
      );
    }
    const anchors = await this.db.select<{
      room_id: string;
      floor_id: string;
      anchor_x: number;
      anchor_y: number;
      floor_area_x100: number | null;
      perimeter_x100: number | null;
      polygon_json: string | null;
      status: string;
    }>(
      "SELECT room_id, floor_id, anchor_x, anchor_y, floor_area_x100, perimeter_x100, polygon_json, status FROM design_room_geometry WHERE design_id = ?",
      [fromDesignId],
    );
    for (const a of anchors) {
      await this.db.execute(
        `INSERT OR IGNORE INTO design_room_geometry (design_id, room_id, floor_id, anchor_x, anchor_y, floor_area_x100, perimeter_x100, polygon_json, status, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [toDesignId, a.room_id, a.floor_id, a.anchor_x, a.anchor_y, a.floor_area_x100, a.perimeter_x100, a.polygon_json, a.status, now],
      );
    }
    return wallMap;
  }

  async rename(id: string, name: string): Promise<void> {
    const problems = validateDesignName(name);
    if (problems.length > 0) throw new ValidationError(problems);
    await this.db.execute("UPDATE designs SET name = ?, updated_at = ? WHERE id = ?", [name.trim(), new Date().toISOString(), id]);
  }

  /** Archive a proposed design (the Existing design cannot be archived; the scope source must be reassigned first). */
  async archive(id: string): Promise<void> {
    const design = await this.getById(id);
    if (!design) return;
    if (design.kind === "existing") throw new ValidationError(["The Existing design cannot be archived — it is the factual base of the project."]);
    const scope = await this.db.select<{ id: string }>("SELECT id FROM projects WHERE active_design_id = ?", [id]);
    if (scope.length > 0) throw new ValidationError(["This design drives Scope & Estimate. Choose another scope design first."]);
    const now = new Date().toISOString();
    await this.db.execute("UPDATE designs SET archived_at = ?, updated_at = ? WHERE id = ? AND archived_at IS NULL", [now, now, id]);
  }

  async unarchive(id: string): Promise<void> {
    await this.db.execute("UPDATE designs SET archived_at = NULL, updated_at = ? WHERE id = ?", [new Date().toISOString(), id]);
  }

  async getActiveDesignId(projectId: string): Promise<string | null> {
    const rows = await this.db.select<{ active_design_id: string | null }>("SELECT active_design_id FROM projects WHERE id = ?", [projectId]);
    return rows[0]?.active_design_id ?? null;
  }

  async setActiveDesign(projectId: string, designId: string): Promise<void> {
    const design = await this.getById(designId);
    if (!design || design.projectId !== projectId || design.archivedAt !== null) {
      throw new ValidationError(["That design cannot drive Scope & Estimate."]);
    }
    await this.db.execute("UPDATE projects SET active_design_id = ?, updated_at = ? WHERE id = ?", [designId, new Date().toISOString(), projectId]);
  }
  /**
   * One committed change to this design (spec §65J). Returns the new
   * revision so the caller can tell live viewers which state they should
   * now be holding.
   */
  async bumpRevision(designId: string): Promise<number> {
    await this.db.execute(
      "UPDATE designs SET revision = revision + 1, updated_at = ? WHERE id = ?",
      [new Date().toISOString(), designId],
    );
    const rows = await this.db.select<{ revision: number }>("SELECT revision FROM designs WHERE id = ?", [designId]);
    return rows.length > 0 ? Number(rows[0].revision) : 0;
  }

  /** The current revision, without changing it. */
  async revisionOf(designId: string): Promise<number> {
    const rows = await this.db.select<{ revision: number }>("SELECT revision FROM designs WHERE id = ?", [designId]);
    return rows.length > 0 ? Number(rows[0].revision ?? 0) : 0;
  }

}
